import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Link,
  type Theme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { not } from "ramda";
import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useErrorHandler } from "src/hooks/errorHandler";
import { useImpersonate } from "src/hooks/useImpersonate";
import { makeStyles } from "tss-react/mui";
import { GET_USER_ADMIN, GET_USER_CALENDAR } from "../gql/queries/user";

import { useOnOffSwitch } from "../helpers/hooks";
import { Spacing } from "../types/enum";
import {
  type UserModel,
  useAdminBanUserMutation,
  useAdminUnbanUserMutation,
  useDeleteAccountMutation,
  useRegenerateCalendarPathMutation,
} from "../types/graphql";
import ConfirmationModal from "./ConfirmationModal";

/**
 * Types
 */
interface Props {
  user: UserModel;
}

/**
 * Styles
 */
const useStyles = makeStyles()((theme: Theme) => ({
  error: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
    },
  },
  warning: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.warning.main,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

const UserPageDetailsInfoOthersCard: React.FC<Props> = ({ user }: Props) => {
  const { classes } = useStyles();
  const history = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { handleError } = useErrorHandler();

  const [isOpenDelete, openDelete, closeDelete] = useOnOffSwitch();
  const [isOpenBan, openBan, closeBan] = useOnOffSwitch();
  const [isOpenUnBan, openUnBan, closeUnBan] = useOnOffSwitch();
  const [isOpenRecreateCalendar, openRecreateCalendar, closeRecreateCalendar] = useOnOffSwitch();

  const [deleteAccount] = useDeleteAccountMutation();
  const impersonateMutation = useImpersonate(user.id);

  const [banUser] = useAdminBanUserMutation({
    refetchQueries: [{ query: GET_USER_ADMIN, variables: { id: user.id } }],
  });

  const [unbanUser] = useAdminUnbanUserMutation({
    refetchQueries: [{ query: GET_USER_ADMIN, variables: { id: user.id } }],
  });

  const [recreateCalendarMutation] = useRegenerateCalendarPathMutation({
    refetchQueries: [{ query: GET_USER_CALENDAR, variables: { userId: user.id } }],
  });

  const recreateCalendar = async (): Promise<any> => {
    await handleError(recreateCalendarMutation, {
      onSuccess: () => {
        enqueueSnackbar("Calendar path was recreated", { variant: "success" });
      },
      finally: () => {
        closeRecreateCalendar();
      },
    })({ variables: { userId: user.id } });
  };

  const onBan = async (): Promise<any> => {
    await handleError(banUser, {
      onSuccess: () => {
        enqueueSnackbar("User was banned successfully", { variant: "success" });
      },
      finally: () => {
        closeBan();
      },
    })({ variables: { id: user.id } });
  };

  const onUnban = async (): Promise<any> => {
    await handleError(unbanUser, {
      onSuccess: () => {
        enqueueSnackbar("User was unbanned successfully", { variant: "success" });
      },
      finally: () => {
        closeUnBan();
      },
    })({ variables: { id: user.id } });
  };

  const onSubmit = async (): Promise<any> => {
    await handleError(deleteAccount, {
      onSuccess: () => {
        history("/users");
        enqueueSnackbar("User was deleted successfully", { variant: "success" });
      },
    })({ variables: { id: user.id } });
  };

  return (
    <Card>
      <CardHeader title="Other actions" />
      <Divider />
      <CardContent>
        <Box display="flex">
          <Link component={RouterLink} to={`/users/${user.id}/edit`} underline="none">
            <Button variant="contained" color="secondary">
              Edit
            </Button>
          </Link>

          <Box ml={Spacing.sm}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                impersonateMutation.mutate();
              }}
            >
              Impersonate
            </Button>
          </Box>

          <Box ml={Spacing.sm}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.warning}
              onClick={openRecreateCalendar}
            >
              Recreate calendar link
            </Button>
            <ConfirmationModal
              open={isOpenRecreateCalendar}
              onSubmit={recreateCalendar}
              onClose={closeRecreateCalendar}
              description="By recreating link the old one will be malfunctioning. Are you sure you want to create a new one?"
              label="Recreate"
            />
          </Box>

          {not(user.isBanned) ? (
            <Box ml={Spacing.sm}>
              <Button
                variant="contained"
                color="secondary"
                className={classes.error}
                onClick={openBan}
              >
                Ban
              </Button>
              <ConfirmationModal
                open={isOpenBan}
                onClose={closeBan}
                onSubmit={onBan}
                description="This action will make the user unable to attend events where it was invited."
                label="Ban"
              />
            </Box>
          ) : (
            <Box ml={Spacing.sm}>
              <Button variant="contained" className={classes.warning} onClick={openUnBan}>
                Unban
              </Button>
              <ConfirmationModal
                open={isOpenUnBan}
                onClose={closeUnBan}
                onSubmit={onUnban}
                description="This action will make the user able to attend events where it was invited."
                label="Unban"
              />
            </Box>
          )}

          <Box ml={Spacing.sm}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.error}
              onClick={openDelete}
            >
              Delete
            </Button>
          </Box>
          <ConfirmationModal open={isOpenDelete} onSubmit={onSubmit} onClose={closeDelete} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserPageDetailsInfoOthersCard;
